import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import { DISCLAIMER_LANGUAGES, DISCLAIMER_TYPE_LABELS, DISCLAIMER_TYPES, } from '../../types';
import './Admin.css';
const defaultForm = {
    typeKey: 'terms',
    language: 'en',
    title: '',
    content: '',
    isActive: true,
};
function truncate(str, max) {
    if (str.length <= max)
        return str;
    return `${str.slice(0, max)}…`;
}
function typeLabel(key) {
    return DISCLAIMER_TYPE_LABELS[key] ?? key;
}
export function Disclaimers() {
    const { accessToken } = useAuthStore();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(defaultForm);
    const fetchRows = useCallback(async () => {
        try {
            const response = await fetch('/api/admin/disclaimers', {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok)
                throw new Error('Disclaimer listesi alınamadı');
            const data = (await response.json());
            setRows(data);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Yükleme hatası');
        }
        finally {
            setLoading(false);
        }
    }, [accessToken]);
    useEffect(() => {
        fetchRows();
    }, [fetchRows]);
    const handleOpenModal = (row) => {
        setError('');
        if (row) {
            setEditing(row);
            setFormData({
                typeKey: (DISCLAIMER_TYPES.includes(row.typeKey)
                    ? row.typeKey
                    : 'terms'),
                language: row.language,
                title: row.title,
                content: row.content,
                isActive: row.isActive,
            });
        }
        else {
            setEditing(null);
            setFormData(defaultForm);
        }
        setShowModal(true);
    };
    const handleCloseModal = () => {
        setShowModal(false);
        setEditing(null);
        setFormData(defaultForm);
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setActionLoading(editing?.id ?? -1);
        const body = {
            typeKey: formData.typeKey,
            language: formData.language,
            title: formData.title.trim(),
            content: formData.content,
            isActive: formData.isActive,
        };
        try {
            const url = editing ? `/api/admin/disclaimers/${editing.id}` : '/api/admin/disclaimers';
            const method = editing ? 'PUT' : 'POST';
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                const data = (await response.json().catch(() => ({})));
                throw new Error(data.message || 'Kayıt başarısız');
            }
            handleCloseModal();
            await fetchRows();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Kayıt başarısız');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleToggle = async (row) => {
        setActionLoading(row.id);
        setError('');
        try {
            const response = await fetch(`/api/admin/disclaimers/${row.id}/toggle`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok)
                throw new Error('Durum değiştirilemedi');
            await fetchRows();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Durum değiştirilemedi');
        }
        finally {
            setActionLoading(null);
        }
    };
    const handleDelete = async (row) => {
        if (!confirm(`"${row.title}" kaydını silmek istediğinize emin misiniz?`))
            return;
        setActionLoading(row.id);
        setError('');
        try {
            const response = await fetch(`/api/admin/disclaimers/${row.id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!response.ok) {
                const data = (await response.json().catch(() => ({})));
                throw new Error(data.message || 'Silinemedi');
            }
            await fetchRows();
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Silinemedi');
        }
        finally {
            setActionLoading(null);
        }
    };
    if (loading) {
        return _jsx("div", { className: "admin-loading", children: "Y\u00FCkleniyor..." });
    }
    return (_jsxs("div", { className: "admin-page", children: [_jsxs("header", { className: "admin-page-header", children: [_jsxs("div", { children: [_jsx("h1", { children: "Disclaimers" }), _jsx("p", { children: "Yasal ve bilgilendirme metinlerini tip ve dil baz\u0131nda y\u00F6netin." })] }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), children: "+ Yeni Disclaimer" })] }), error && _jsx("div", { className: "admin-error", children: error }), _jsxs("section", { className: "admin-table-section", children: [_jsx("div", { className: "admin-table-toolbar", children: _jsxs("h2", { children: ["Disclaimers (", rows.length, ")"] }) }), rows.length === 0 ? (_jsxs("div", { className: "admin-empty-state", children: [_jsx("p", { children: "Hen\u00FCz disclaimer yok." }), _jsx("button", { className: "admin-btn admin-btn-primary", onClick: () => handleOpenModal(), children: "\u0130lk kayd\u0131 ekle" })] })) : (_jsx("div", { className: "admin-table-container", children: _jsxs("table", { className: "admin-table", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Tip" }), _jsx("th", { children: "Dil" }), _jsx("th", { children: "Ba\u015Fl\u0131k" }), _jsx("th", { children: "\u0130\u00E7erik" }), _jsx("th", { children: "Durum" }), _jsx("th", { children: "\u0130\u015Flemler" })] }) }), _jsx("tbody", { children: rows.map((row) => (_jsxs("tr", { children: [_jsx("td", { children: _jsx("span", { className: "provider-type-badge", children: typeLabel(row.typeKey) }) }), _jsx("td", { children: _jsx("code", { className: "admin-code-inline", children: row.language }) }), _jsx("td", { children: _jsx("span", { className: "provider-name", children: row.title }) }), _jsx("td", { children: _jsx("span", { className: "provider-base-url", title: row.content, children: truncate(row.content, 64) }) }), _jsx("td", { children: _jsx("button", { type: "button", className: `admin-toggle-btn ${row.isActive ? 'active' : ''}`, onClick: () => handleToggle(row), disabled: actionLoading === row.id, title: row.isActive ? 'Deaktif Et' : 'Aktif Et', children: row.isActive ? '✓ Aktif' : '✗ Pasif' }) }), _jsx("td", { children: _jsxs("div", { className: "admin-action-buttons", children: [_jsx("button", { type: "button", className: "admin-edit-btn", onClick: () => handleOpenModal(row), disabled: actionLoading === row.id, title: "D\u00FCzenle", children: "\u270F\uFE0F" }), _jsx("button", { type: "button", className: "admin-delete-btn", onClick: () => handleDelete(row), disabled: actionLoading === row.id, title: "Sil", children: actionLoading === row.id ? '...' : '🗑' })] }) })] }, row.id))) })] }) }))] }), showModal && (_jsx("div", { className: "admin-modal-overlay", onClick: handleCloseModal, children: _jsxs("div", { className: "admin-modal-content", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "admin-modal-header", children: [_jsx("h2", { children: editing ? 'Disclaimer düzenle' : 'Yeni disclaimer' }), _jsx("button", { type: "button", className: "admin-modal-close", onClick: handleCloseModal, children: "\u2715" })] }), _jsxs("form", { onSubmit: handleSubmit, className: "admin-form", children: [_jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Tip *", _jsx("select", { className: "admin-form-select", value: formData.typeKey, onChange: (e) => setFormData({ ...formData, typeKey: e.target.value }), required: true, children: DISCLAIMER_TYPES.map((t) => (_jsx("option", { value: t, children: DISCLAIMER_TYPE_LABELS[t] }, t))) })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Dil *", _jsx("select", { className: "admin-form-select", value: formData.language, onChange: (e) => setFormData({ ...formData, language: e.target.value }), required: true, children: DISCLAIMER_LANGUAGES.map((l) => (_jsx("option", { value: l.value, children: l.label }, l.value))) })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["Ba\u015Fl\u0131k *", _jsx("input", { type: "text", className: "admin-form-input", value: formData.title, onChange: (e) => setFormData({ ...formData, title: e.target.value }), required: true, maxLength: 256, placeholder: "\u00D6rn: Terms of Service" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-form-label", children: ["\u0130\u00E7erik *", _jsx("textarea", { className: "admin-form-textarea", value: formData.content, onChange: (e) => setFormData({ ...formData, content: e.target.value }), required: true, rows: 12, placeholder: "Metin i\u00E7eri\u011Fi\u2026" })] }) }), _jsx("div", { className: "admin-form-group", children: _jsxs("label", { className: "admin-setting-checkbox", children: [_jsx("input", { type: "checkbox", checked: formData.isActive, onChange: (e) => setFormData({ ...formData, isActive: e.target.checked }) }), _jsx("span", { children: "Aktif" })] }) }), _jsxs("div", { className: "admin-form-actions", children: [_jsx("button", { type: "button", className: "admin-btn admin-btn-secondary", onClick: handleCloseModal, disabled: actionLoading !== null, children: "\u0130ptal" }), _jsx("button", { type: "submit", className: "admin-btn admin-btn-primary", disabled: actionLoading !== null, children: actionLoading !== null ? 'Kaydediliyor…' : editing ? 'Güncelle' : 'Kaydet' })] })] })] }) }))] }));
}
